import { PurchasePreparationState } from '@spa/components/pages/order/model/purchase-execution/execute-purchase'
import { Checkout } from '@spa/components/pages/order/order/models/checkout-store'
import { Either, left, right } from 'fp-ts/lib/Either'
import { fromNullable, Option } from 'fp-ts/lib/Option'
import { cloneDeep } from 'lodash'

/**
 * atone、ペイディなど注文確認画面内でSDKを呼び出してコールバックを得るタイプの決済種別について、
 * コールバック時に必要な情報にアクセスできるようにするための簡易的な状態オブジェクト
 *
 * 元はVueコンポーネント内で状態を保持する簡易的なものだったが、後にatoneのpaymentAuthTokenを含めるようになった。
 * 注文実行時にはpaymentAuthTokenが必ず存在しなければならないというバリデーションはこのクラスの責務に含めていない。
 */
export class CheckoutProcessState<T extends ProcessingState> {
  static forAtone(): CheckoutProcessState<AtoneProcessingState> {
    return new CheckoutProcessState<AtoneProcessingState>(null)
  }

  static forPaidy(): CheckoutProcessState<PaidyProcessingState> {
    return new CheckoutProcessState<PaidyProcessingState>(null)
  }

  private constructor(
    private processingState: T | null
  ) {}

  /**
   * @return Either<Error, undefined> 実行中のプロセスが存在しないときはエラーを返す
   */
  pushState(state: T): Either<Error, undefined> {
    if (this.processingState !== null) {
      return left(
        new Error('The process is already running')
      )
    }

    this.processingState = state

    return right(undefined)
  }

  /**
   * @return Either<Error, T> 実行中のプロセスが存在しないときはエラーを返す
   */
  popState(): Either<Error, T> {
    if (this.processingState === null) {
      return left(new Error('No process is running'))
    }
    const state = cloneDeep(this.processingState)
    this.processingState = null

    return right(state)
  }

  /**
   * @return Either<Error, undefined> 実行中のプロセスが存在しないときはエラーを返す
   */
  partialUpdate(partialState: Partial<T>): Either<Error, undefined> {
    return this.popState().fold(
      error => left(error),
      (state) => {
        this.processingState = {
          ...state,
          ...partialState,
        }

        return right(undefined)
      }
    )
  }

  /**
   * popせずに読み込みたいときに使う
   */
  readState(): Option<ProcessingState> {
    return fromNullable(this.processingState)
  }

  /**
   * エラーになったときなど、とにかく初期状態に戻したいがその際にエラーは投げてほしくない場合に使う
   */
  reset(): void {
    this.processingState = null
  }
}

export type ProcessingState = AtoneProcessingState | PaidyProcessingState

export interface AtoneProcessingState {
  purchasePreparationState: PurchasePreparationState
  checkout: Checkout
  orderId: string
  /** atoneからのauthenticatedコールバックをハンドリングして上書きすること */
  paymentAuthToken: Option<string>
}

export interface PaidyProcessingState {
  purchasePreparationState: PurchasePreparationState
  checkout: Checkout
  orderId: string
}
